import {makeAutoObservable, runInAction} from "mobx";
import {
    AUTO_PROCESS_TRIGGER,
    AUTO_PROCESS_VALIDATED_QUALIFIER,
    BXGY_COMPENSATE_BY,
    CUSTOMER_ACTIONS,
    EXCHANGE_BALANCING,
    EXCHANGE_DISCOUNT_TYPE,
    ICancelInactiveRmasSettings,
    INCLUDE_PRODUCT_DUTIES_IN_REFUND,
    INCLUDE_SHIPPING_COST_IN_REFUND,
    IReturnScoreSetting,
    ISetting,
    ISOLATION_MODE,
    LABEL_FEE_ROUNDING,
    LINE_ITEMS_REFUND_TRANSACTIONS,
    NOTIFY_REFUND_WHEN,
    ORDER_DELIVERY_FALLBACK,
    OUT_OF_POLICY,
    PRICE_CALCULATIONS,
    PRICE_CHANGE_SETTING,
    REFUND_LEVELS,
    REFUND_TYPES,
    REFUNDS_PRIORITY,
    RESTOCK_BY,
    RETURN_LABEL_FEE_PAYMENT,
    VALIDATION_RESTOCK_TRIGGER,
} from "../models/User";
import SettingsService from "../services/api/settings-service";
import {rootContainer, TYPE} from "../services/container/root";
import CreateRmaDefaultSettingsEditorStore from "./create-rma-default-settings-editor-store";
import CurrencyStore from "./currency-store";
import RefundSettingStore from "./refund-setting-store";
import SearchByStore from "./search-by-store";
import BrowseByCollectionStore from "./browse-by-collection-store";
import InstantExchangeStore from "./instant-exchange-store";
import ReturngoFeesSettingStore from "./returngo-fees-setting-store";
import SwitchToExchangeStore from "./switch_to_exchange_store";
import CapDiscountStore from "./cap-discount-store";
import RefundToBankSettingStore from "./refund-to-bank-setting-store";
import RmaLevelBonusLimitStore from "./rma-level-bonus-limit-store";
import NotificationOnlyModeSettingsStore from './notification-only-mode-settings-store';
import AnyToAnyExchangesStore from './any-to-any-exchanges-store';
import TimezoneStore from "./timezone-store";
import {toBoolean} from "../models/model-types-methods";
import CancelInactiveRmasSettingStore from "./cancel-inactive-rmas-setting-store";
import {SingleValue} from "react-select";
import {IOption} from "../components/inputs/select-dropdown/select-dropdown";
import ExchangeLocationsStore from "../pages/settings/store/exchange-locations/stores/exchange-locations-store";
import {WithOld} from "./types";

interface ISettingChanges extends Partial<ISetting> { };

interface ICancelInactiveRmasSettingsMap {
    [key: string]: CancelInactiveRmasSettingStore;
}

class SettingStore {
    private _changes: ISettingChanges = {};
    private _model: ISetting = {} as ISetting;
    private _currencies: CurrencyStore[] | [] = [];
    private _refundSettings: RefundSettingStore[] | [] = [];
    private _search_by!: SearchByStore;
    private _browseByCollection!: BrowseByCollectionStore;
    private _returngoFeesSettings: ReturngoFeesSettingStore[] | [] = [];
    private _isSaving: boolean = false;
    private _isReturnScoreSaving: boolean = false;
    private _instantExchangeStore: InstantExchangeStore = {} as InstantExchangeStore;
    private _switchToExchangeStore: SwitchToExchangeStore = {} as SwitchToExchangeStore;
    private _capDiscountStore: CapDiscountStore = {} as CapDiscountStore;
    private _refundToBankSettingStore: RefundToBankSettingStore = {} as RefundToBankSettingStore;
    private _createRmaDefaultSettingsEditorStore: CreateRmaDefaultSettingsEditorStore = {} as CreateRmaDefaultSettingsEditorStore;
    private _rmaLevelBonusLimitStore: RmaLevelBonusLimitStore = {} as RmaLevelBonusLimitStore;
    private _notificationOnlyModeSettingsStore: NotificationOnlyModeSettingsStore = {} as NotificationOnlyModeSettingsStore;
    private _anyToAnyExchangesStore: AnyToAnyExchangesStore = {} as AnyToAnyExchangesStore;
    private _exchangeLocationsStore: ExchangeLocationsStore = {} as ExchangeLocationsStore;

    private _timezoneStore!: TimezoneStore;
    private _cancel_inactive_rmas_settings!: ICancelInactiveRmasSettingsMap;

    constructor(model: ISetting) {
        makeAutoObservable(this, {}, { autoBind: true });
        this.setModel(model);
    }

    private get service(): SettingsService {
        return rootContainer.get<SettingsService>(TYPE.SettingsService);
    }

    public isLoading(): boolean {
        return this._createRmaDefaultSettingsEditorStore.isLoading();
    }
    public isSaving(): boolean {
        return this._isSaving;
    }

    public isReturnScoreSaving(): boolean {
        return this._isReturnScoreSaving;
    }

    private setModel(model: ISetting): void {
        this._model = ensureValidModel(model);
        this._currencies = this._model.currencies.map(c => new CurrencyStore(c));
        this._search_by = new SearchByStore(this._model.search_product_by);
        this._browseByCollection = new BrowseByCollectionStore(this._model.is_browse_by_collection_active, this._model.merchant_collections);
        this._refundSettings = this._model.refund_settings.map(r => new RefundSettingStore(r));
        this._instantExchangeStore = new InstantExchangeStore(this._model.instant_exchange_setting);
        this._returngoFeesSettings = this._model.returngo_fees_settings.map(r => new ReturngoFeesSettingStore(r));
        this._switchToExchangeStore = new SwitchToExchangeStore(this._model.switch_to_exchange_setting, this.currencyCode);
        this._capDiscountStore = new CapDiscountStore(this._model.cap_discount_setting);
        this._refundToBankSettingStore = new RefundToBankSettingStore(this._model.refund_to_bank_setting);
        this._createRmaDefaultSettingsEditorStore = new CreateRmaDefaultSettingsEditorStore(this._model.create_rma_settings);
        this._rmaLevelBonusLimitStore = new RmaLevelBonusLimitStore(this._model.rma_level_bonus_limit_setting);
        this._notificationOnlyModeSettingsStore = new NotificationOnlyModeSettingsStore(this._model.notification_only_mode_setting);
        this._anyToAnyExchangesStore = new AnyToAnyExchangesStore(this._model.any_to_any_exchanges_setting);
        this._exchangeLocationsStore = new ExchangeLocationsStore(this._model.merchant_filtering_exchange_locations);
        this._timezoneStore = new TimezoneStore(this._model);

        /** Set cancel inactiove RMAs setting stores */
        const customerActionFeeInvoiceSentSetting = model.cancel_inactive_rmas_settings?.find(s => s.customer_action === CUSTOMER_ACTIONS.CUSTOMER_ACTION_PAY_CHARGED_FEES_INVOICE);
        const customerActionExchangeOrderInvoiceSentSetting = model.cancel_inactive_rmas_settings?.find(s => s.customer_action === CUSTOMER_ACTIONS.CUSTOMER_ACTION_PAY_EXCHANGE_ORDER_INVOICE);
        const customerActionShipmentInTransitSetting = model.cancel_inactive_rmas_settings?.find(s => s.customer_action === CUSTOMER_ACTIONS.CUSTOMER_ACTION_SHIPMENT_IN_TRANSIT);
        const customerActionShipmentDeliveredDomesticSetting = model.cancel_inactive_rmas_settings?.find(s => s.customer_action === CUSTOMER_ACTIONS.CUSTOMER_ACTION_SHIPMENT_DELIVERED_DOMESTIC);
        const customerActionShipmentDeliveredInternationalSetting = model.cancel_inactive_rmas_settings?.find(s => s.customer_action === CUSTOMER_ACTIONS.CUSTOMER_ACTION_SHIPMENT_DELIVERED_INTERNATIONAL);
        this._cancel_inactive_rmas_settings = {
            [CUSTOMER_ACTIONS.CUSTOMER_ACTION_PAY_EXCHANGE_ORDER_INVOICE]: new CancelInactiveRmasSettingStore(CUSTOMER_ACTIONS.CUSTOMER_ACTION_PAY_EXCHANGE_ORDER_INVOICE, customerActionExchangeOrderInvoiceSentSetting),
            [CUSTOMER_ACTIONS.CUSTOMER_ACTION_PAY_CHARGED_FEES_INVOICE]: new CancelInactiveRmasSettingStore(CUSTOMER_ACTIONS.CUSTOMER_ACTION_PAY_CHARGED_FEES_INVOICE, customerActionFeeInvoiceSentSetting),
            [CUSTOMER_ACTIONS.CUSTOMER_ACTION_SHIPMENT_IN_TRANSIT]: new CancelInactiveRmasSettingStore(CUSTOMER_ACTIONS.CUSTOMER_ACTION_SHIPMENT_IN_TRANSIT, customerActionShipmentInTransitSetting),
            [CUSTOMER_ACTIONS.CUSTOMER_ACTION_SHIPMENT_DELIVERED_DOMESTIC]: new CancelInactiveRmasSettingStore(CUSTOMER_ACTIONS.CUSTOMER_ACTION_SHIPMENT_DELIVERED_DOMESTIC, customerActionShipmentDeliveredDomesticSetting),
            [CUSTOMER_ACTIONS.CUSTOMER_ACTION_SHIPMENT_DELIVERED_INTERNATIONAL]: new CancelInactiveRmasSettingStore(CUSTOMER_ACTIONS.CUSTOMER_ACTION_SHIPMENT_DELIVERED_INTERNATIONAL, customerActionShipmentDeliveredInternationalSetting)
        }

        this.discardChanges();
    }

    public getInactiveRmasSetting(customerAction: CUSTOMER_ACTIONS): CancelInactiveRmasSettingStore {
        return this._cancel_inactive_rmas_settings[customerAction];
    }

    public get timezoneStore(): TimezoneStore {
        return this._timezoneStore;
    }

    public get searchBy(): SearchByStore {
        return this._search_by;
    }

    public get browseByCollection(): BrowseByCollectionStore {
        return this._browseByCollection;
    }

    public get instantExchangeStore(): InstantExchangeStore{
        return this._instantExchangeStore;
    }

    public get switchToExchangeStore(): SwitchToExchangeStore {
        return this._switchToExchangeStore;
    }

    public get capDiscountStore(): CapDiscountStore {
        return this._capDiscountStore;
    }

    public get refundToBankSettingStore(): RefundToBankSettingStore {
        return this._refundToBankSettingStore;
    }

    public get createRmaDefaultSettingsEditorStore(): CreateRmaDefaultSettingsEditorStore {
        return this._createRmaDefaultSettingsEditorStore;
    }

    public get rmaLevelBonusesLimitStore(): RmaLevelBonusLimitStore {
        return this._rmaLevelBonusLimitStore;
    }

    public get notificationOnlyModeSettingStore(): NotificationOnlyModeSettingsStore {
        return this._notificationOnlyModeSettingsStore;
    }

    public get anyToAnyExchangesStore(): AnyToAnyExchangesStore {
        return this._anyToAnyExchangesStore;
    }

    public get exchangeLocationsStore(): ExchangeLocationsStore {
        return this._exchangeLocationsStore;
    }

    public get currencyCode(): string | undefined {
        /** store has a single currency */
        return this._currencies[0]?.currency_code;
    }

    public get returnPaidBy(): string {
        return this._changes.return_paid_by ?? this._model.return_paid_by;
    }

    public get storeCostMargin(): string {
        return this._changes.store_cost_margin ?? this._model.store_cost_margin;
    }

    public get returnedItemValue(): number {
        return this._changes.returned_item_value ?? this._model.returned_item_value;
    }

    public get costOfHandlingAReturn(): number {
        return this._changes.cost_of_handling_a_return ?? this._model.cost_of_handling_a_return;
    }

    public get giftCardsExpireType(): string {
        return this._changes.gift_cards_expire_type ?? this._model.gift_cards_expire_type;
    }

    public get storeName(): string {
        return this._changes.store_name ?? this._model.store_name;
    }

    public get storeUrl(): string {
        return this._changes.store_url ?? this._model.store_url;
    }

    public get adminPath(): string {
        return this._changes.admin_path ?? this._model.admin_path;
    }

    public get returnPolicyUrl(): string {
        return this._changes.return_policy_url ?? this._model.return_policy_url;
    }

    public get customerSupport(): string {
        return this._changes.customer_support ?? this._model.customer_support;
    }

    public get giftCardsExpire(): string {
        return this._changes.gift_cards_expire ?? this._model.gift_cards_expire;
    }

    public get returnCreditsExpire(): number {
        return this._changes.return_credits_expire ?? this._model.return_credits_expire;
    }

    public get returnCreditsExpireType(): string {
        return this._changes.return_credits_expire_type ?? this._model.return_credits_expire_type;
    }

    public get calculatePriceDiffBy(): PRICE_CALCULATIONS {
        return this._changes.calculate_price_diff_by ?? this._model.calculate_price_diff_by;
    }

    public get calculateProductExchangePriceDiffBy(): PRICE_CALCULATIONS {
        return this._changes.calculate_product_exchange_price_diff_by ?? this._model.calculate_product_exchange_price_diff_by;
    }

    public get returnLabelFeePayment(): RETURN_LABEL_FEE_PAYMENT {
        return this._changes.return_label_fee_payment ?? this._model.return_label_fee_payment;
    }

    public get defaultRefundLevel(): REFUND_LEVELS {
        return this._changes.default_refund_level ?? this._model.default_refund_level;
    }

    public get orderDeliveryFallback(): ORDER_DELIVERY_FALLBACK {
        return this._changes.order_delivery_fallback ?? this._model.order_delivery_fallback;
    }

    public get refundsPriority(): REFUNDS_PRIORITY {
        return this._changes.refunds_priority ?? this._model.refunds_priority;
    }

    public get defaultPortalResolutionId(): number | null {
        // Read draft so MobX tracks it. undefined = no draft; null = intentional None (do not use ??).
        const draft = this._changes.default_portal_resolution_id;
        if (draft !== undefined) {
            return draft as number | null;
        }
        return this._model.default_portal_resolution_id ?? null;
    }

    public get outOfPolicy(): OUT_OF_POLICY {
        return this._changes.out_of_policy_item ?? this._model.out_of_policy_item;
    }

    public get restockBy(): RESTOCK_BY {
        return this._changes.restock_by ?? this._model.restock_by;
    }

    public get validationRestockTrigger(): VALIDATION_RESTOCK_TRIGGER {
        return this._changes.validation_restock_trigger ?? this._model.validation_restock_trigger;
    }

    public get notifyRefundWhen(): NOTIFY_REFUND_WHEN {
        return this._changes.notify_refund_when ?? this._model.notify_refund_when;
    }

    public get supportMultipleCurrencies(): boolean {
        return this._changes.support_multiple_currencies ?? this._model.support_multiple_currencies;
    }

    public get allowPortalRedirection(): boolean {
        return this._changes.allow_portal_redirection ?? this._model.allow_portal_redirection;
    }

    public get roundConvertedPieces(): boolean {
        return this._changes.is_rounding ?? this._model.is_rounding;
    }

    public get allowMultipleReturnRequests(): boolean {
        return this._changes.allow_multiple_return_requests ?? this._model.allow_multiple_return_requests;
    }

    public get enableStartAnotherReturn(): boolean {
        return this._changes.enable_start_another_return ?? this._model.enable_start_another_return;
    }

    public get requireManagementApproval(): boolean {
        return this._changes.require_manager_approval ?? this._model.require_manager_approval;
    }

    public get showDisabledResolution(): boolean {
        return this._changes.show_disabled_resolution ?? this._model.show_disabled_resolution;
    }

    public get enableOrderNotes(): boolean {
        return !(this._changes.disable_order_notes ?? this._model.disable_order_notes);
    }

    public get allowRefundOfExchanged(): boolean {
        return this._changes.allow_refund_of_exchanged ?? this._model.allow_refund_of_exchanged;
    }

    public get allowExchangeOfExchanged(): boolean {
        return this._changes.allow_exchange_of_exchanged ?? this._model.allow_exchange_of_exchanged;
    }

    public get autoPopulateRefundAmount(): boolean {
        return this._changes.auto_populate_refund_amount ?? this._model.auto_populate_refund_amount;
    }

    public get enableGiftReturns(): boolean {
        return this._changes.enable_gift_returns ?? this._model.enable_gift_returns;
    }

    public get isValidationEnabled(): boolean {
        return this._changes.is_validation_enabled ?? this._model.is_validation_enabled;
    }

    public get isRestockNoteMandatory(): boolean {
        return this._changes.is_restock_note_mandatory ?? this._model.is_restock_note_mandatory;
    }

    public get isValidateAllEnabled(): boolean {
        return this._changes.is_validate_all_enabled ?? this._model.is_validate_all_enabled;
    }

    public get isBxgyEnabled(): boolean {
        return this._changes.is_bxgy_enabled ?? this._model.is_bxgy_enabled;
    }

    public get bxgyBreakable(): boolean {
        return this._changes.bxgy_breakable ?? this._model.bxgy_breakable;
    }

    public get bxgyCompensateBy(): BXGY_COMPENSATE_BY {
        return this._changes.bxgy_compensate_by ?? this._model.bxgy_compensate_by;
    }

    public get orderDeliveryFallbackDays(): number {
        return this._changes.order_delivery_fallback_days ?? this._model.order_delivery_fallback_days;
    }

    public get shipmentInTransitFallback(): number {
        return this._changes.shipment_in_transit_fallback ?? this._model.shipment_in_transit_fallback;
    }

    public get returnPolicyPeriod(): number {
        return this._changes.return_policy_period ?? this._model.return_policy_period;
    }

    public get apiCollectionsAsArray(): boolean {
        return this._changes.api_strict_types ?? this._model.api_strict_types;
    }

    public get includeShippingCostInRefund(): INCLUDE_SHIPPING_COST_IN_REFUND {
        return this._changes.include_shipping_cost_in_refund ?? this._model.include_shipping_cost_in_refund;
    }

    public get includeProductDutiesInRefund(): INCLUDE_PRODUCT_DUTIES_IN_REFUND {
        return (
            this._changes.include_product_duties_in_refund ??
            this._model.include_product_duties_in_refund ??
            INCLUDE_PRODUCT_DUTIES_IN_REFUND.SET_PER_POLICY_RULE
        );
    }

    public get isProductDutiesRefundSupported(): boolean {
        return (
            this._changes.is_product_duties_refund_supported ??
            this._model.is_product_duties_refund_supported ??
            false
        );
    }

    public get includeShippingCostInRefundWhenAllItemsAreReturned(): boolean {
        return this._changes.include_shipping_cost_in_refund_for_all_items_only ?? this._model.include_shipping_cost_in_refund_for_all_items_only;
    }

    public get requestCustomersConfirmPolicy(): boolean {
        return this._changes.request_customers_confirm_policy ?? this._model.request_customers_confirm_policy;
    }

    public get shippingCostRefundSetting(): RefundSettingStore[] {
        /* Auto-refund is not supported for bank account resolution for now. Put this option in the back */
        return this._refundSettings.slice().sort((a, b) => (a.refundType > b.refundType && b.refundType !== REFUND_TYPES.BANK_REFUND) ? 1 : -1);
    }

    public get returngoFeesSetting(): ReturngoFeesSettingStore[] {
        return this._returngoFeesSettings;
    }

    public get lineItemRefundTransactions(): LINE_ITEMS_REFUND_TRANSACTIONS {
        return this._changes.line_items_refund_transactions ?? this._model.line_items_refund_transactions;
    }

    public get isBrowseByCollectionActive(): boolean {
        return this._browseByCollection.getUpdatedModel().is_browse_by_collection_active ?? false;
    }

    public get variantExchangePriceChange(){
        return this._changes.variant_exchange_changes_to_new_item_price ?? this._model.variant_exchange_changes_to_new_item_price;
    }
    public get productExchangePriceChange(){
        return this._changes.product_exchange_changes_to_new_item_price ?? this._model.product_exchange_changes_to_new_item_price;
    }

    public get exchangeBalancing(): EXCHANGE_BALANCING {
        return this._changes.exchange_balancing ?? this._model.exchange_balancing;
    }

    public get exchangeDiscountType(): EXCHANGE_DISCOUNT_TYPE {
        return this._changes.exchange_discount_type ?? this._model.exchange_discount_type;
    }

    public get isStrictDeliveryDate(): boolean {
        return this._changes.strict_delivery_date ?? this._model.strict_delivery_date;
    }

    public get capturePaymentTrigger(): AUTO_PROCESS_TRIGGER {
        return this._changes.capture_payment_trigger ?? this._model.capture_payment_trigger;
    }

    public get requireInstantExchange(): boolean {
        return this._changes.require_instant_exchange ?? this._model.require_instant_exchange;
    }

    public get capturePaymentValidatedQualifier() : AUTO_PROCESS_VALIDATED_QUALIFIER {
        return this._changes.capture_payment_validated_qualifier ?? this._model.capture_payment_validated_qualifier;
    }

    public get enablePortalCheckout(): boolean {
        return this._changes.enable_portal_checkout ?? this._model.enable_portal_checkout;
    }

    public get minimumPaymentThreshold(): number {
        return this._changes.minimum_payment_threshold ?? this._model.minimum_payment_threshold;
    }

    public get usePreAuthorize(): boolean {
        return this._changes.use_pre_authorize ?? this._model.use_pre_authorize;
    }

    public get portalIsolationMode(): ISOLATION_MODE {
        return this._changes.portal_isolation_mode ?? this._model.portal_isolation_mode;
    }

    public get autoSelectSamePolicy(): boolean {
        return this._changes.auto_select_same_policy ?? this._model.auto_select_same_policy;
    }

    public get cancelInactiveRmas(): boolean {
        return this._changes.cancel_inactive_rmas ?? this._model.cancel_inactive_rmas;
    }

    public get fulfillExchangesBasedOnOrderCountry(): boolean {
        return this._changes.fulfill_exchanges_based_on_order_country ?? this._model.fulfill_exchanges_based_on_order_country;
    }
    public get isCancelShipmentLabelEnabled(): boolean {
        return this._changes.is_cancel_shipment_label_enabled ?? this._model.is_cancel_shipment_label_enabled;
    }

    public get showShipToAddress(): boolean { return this._changes.show_ship_to_address ?? this._model.show_ship_to_address; }

    public get enablePortalV2(): boolean { return this._changes.enable_portal_v2 ?? this._model.enable_portal_v2; }

    /** unchangable properties for view only */
    public get returnsPortalPreviewLink(): string { return this._model._returns_portal_preview_link; }
    public get returnsPortalLink(): string { return this._model._returns_portal_link; }
    public get trackingPortalPreviewLink(): string { return this._model._tracking_portal_preview_link; }
    public get isMerchantOnboarded(): boolean { return this._model._is_merchant_onboarded; }
    public get showRefundNoteBeforeRestocking(): boolean { return this.restockBy === RESTOCK_BY.RESTOCK_BY_REFUND_PROCESS}
    public get storeCreditsRefundLimitPercentage(): number { return this._changes.store_credits_refund_limit_percentage ?? this._model.store_credits_refund_limit_percentage; }
    public get giftCardRefundLimitPercentage(): number { return this._changes.gift_card_refund_limit_percentage ?? this._model.gift_card_refund_limit_percentage; }

    public get storeFavicon(): string | null {
        if (this._changes._store_delete_favicon) {
            return null;
        }

        if (this._changes._store_new_favicon) {
            return this._getBase64Str(this._changes._store_new_favicon);
        }

        return this._model.store_favicon;
    }

    public get storePageTitle(): string {
        return (
            this._changes.store_page_title ?? this._model.store_page_title ?? ""
        );
    }

    public get hasDiscreetSenderEnabled(): boolean {
        return this._changes.enabled_discreet_sender ?? this._model.enabled_discreet_sender;
    }

    public setFulfillExchangesBasedOnOrderCountry(v: boolean) {
        this._changes.fulfill_exchanges_based_on_order_country = v;
        this._clearChangesIfEqual('fulfill_exchanges_based_on_order_country');
    }

    public setCancelInactiveRmas(v: boolean) {
        this._changes.cancel_inactive_rmas = v;
        this._clearChangesIfEqual('cancel_inactive_rmas');
    }
    public setIsStrictDeliveryDate(v: boolean ) {
        this._changes.strict_delivery_date = v;
        this._clearChangesIfEqual('strict_delivery_date');
    }

    public setReturnPaidBy(v: string) {
        this._changes.return_paid_by = v;
    }

    public setStoreCostMargin(v: string) {
        this._changes.store_cost_margin = v;
    }

    public setReturnedItemValue(v: number) {
        this._changes.returned_item_value = v;
    }

    public setCostOfHandlingAReturn(v: number) {
        this._changes.cost_of_handling_a_return = v;
    }

    public setGiftCardsExpireType(v: string) {
        this._changes.gift_cards_expire_type = v;
        this._clearChangesIfEqual('gift_cards_expire_type');
    }

    public setStoreName(v: string) {
        this._changes.store_name = v;
        this._clearChangesIfEqual('store_name');
    }

    public setStoreUrl(v: string) {
        this._changes.store_url = v;
        this._clearChangesIfEqual('store_url');
    }

    public setAdminPath(v: string) {
        this._changes.admin_path = v;
        this._clearChangesIfEqual('admin_path');
    }

    public setReturnPolicyUrl(v: string) {
        this._changes.return_policy_url = v;
        this._clearChangesIfEqual('return_policy_url');
    }

    public setCustomerSupport(v: string) {
        this._changes.customer_support = v.replace(/\s/, '');
        this._clearChangesIfEqual('customer_support');
    }

    public setGiftCardsExpire(v: string) {
        if (!isStringContainsInteger(v)) {
            return;
        }
        this._changes.gift_cards_expire = v;
        this._clearChangesIfEqual('gift_cards_expire');
    }

    public setReturnCreditsExpire(v: number) {
        if (!isStringContainsInteger(v)) {
            return;
        }
        this._changes.return_credits_expire = v;
        this._clearChangesIfEqual('return_credits_expire');
    }

    public setReturnCreditsExpireType(v: string) {
        this._changes.return_credits_expire_type = v;
        this._clearChangesIfEqual('return_credits_expire_type');
    }

    public setCalculatePriceDiffBy(v: PRICE_CALCULATIONS) {
        this._changes.calculate_price_diff_by = v;
        this._clearChangesIfEqual('calculate_price_diff_by');
    }

    public setCalculateProductExchangePriceDiffBy(v: PRICE_CALCULATIONS) {
        this._changes.calculate_product_exchange_price_diff_by = v;
        this._clearChangesIfEqual('calculate_product_exchange_price_diff_by');
    }

    public setReturnLabelFeePayment(v: RETURN_LABEL_FEE_PAYMENT) {
        this._changes.return_label_fee_payment = v;
        this._clearChangesIfEqual('return_label_fee_payment');
    }

    public setDefaultRefundLevel(v: REFUND_LEVELS) {
        this._changes.default_refund_level = v;
        this._clearChangesIfEqual('default_refund_level');
    }

    public setOrderDeliveryFallback(v: ORDER_DELIVERY_FALLBACK) {
        this._changes.order_delivery_fallback = v;
        this._clearChangesIfEqual('order_delivery_fallback');
    }

    public setRefundsPriority(v: REFUNDS_PRIORITY) {
        this._changes.refunds_priority = v;
        this._clearChangesIfEqual('refunds_priority');
    }

    public setDefaultPortalResolutionId(v: number | null) {
        this._changes.default_portal_resolution_id = v === null || v === undefined ? null : Number(v);
        this._clearChangesIfEqual('default_portal_resolution_id');
    }

    /** Sync after BE cleared the default (e.g. resolution delete) without dirtying Save. */
    public applyServerDefaultPortalResolutionId(v: number | null) {
        this._model.default_portal_resolution_id = v === null || v === undefined ? null : Number(v);
        delete this._changes.default_portal_resolution_id;
    }

    public setOutOfPolicy(v: OUT_OF_POLICY) {
        this._changes.out_of_policy_item = v;
        this._clearChangesIfEqual('out_of_policy_item');
    }

    public setRestockBy(v: RESTOCK_BY) {
        this._changes.restock_by = v;
        this._clearChangesIfEqual('restock_by');
    }

    public setValidationRestockTrigger(v: VALIDATION_RESTOCK_TRIGGER) {
        this._changes.validation_restock_trigger = v;
        this._clearChangesIfEqual('validation_restock_trigger');
    }

    public setNotifyRefundWhen(v: NOTIFY_REFUND_WHEN) {
        this._changes.notify_refund_when = v;
        this._clearChangesIfEqual('notify_refund_when');
    }

    public setSupportMultipleCurrencies(v: boolean) {
        this._changes.support_multiple_currencies = v;
        this._clearChangesIfEqual('support_multiple_currencies');
    }

    public setAllowPortalRedirection(v: boolean) {
        this._changes.allow_portal_redirection = v;
        this._clearChangesIfEqual('allow_portal_redirection');
    }

    public setRoundConvertedPieces(v: boolean) {
        this._changes.is_rounding = v;
        this._clearChangesIfEqual('is_rounding');
    }

    public setAllowMultipleReturnRequests(v: boolean) {
        this._changes.allow_multiple_return_requests = v;
        this._clearChangesIfEqual('allow_multiple_return_requests');

        if (!v) {
            this.setEnableStartAnotherReturn(false);
        }
    }

    public setEnableStartAnotherReturn(v: boolean) {
        this._changes.enable_start_another_return = v;
        this._clearChangesIfEqual('enable_start_another_return');
    }

    public setRequireManagementApproval(v: boolean) {
        this._changes.require_manager_approval = v;
        this._clearChangesIfEqual('require_manager_approval');
    }

    public setEnableOrderNotes(v: boolean) {
        this._changes.disable_order_notes = !v;
        this._clearChangesIfEqual('disable_order_notes');
    }

    public setAllowRefundOfExchanged(v: boolean) {
        this._changes.allow_refund_of_exchanged = v;
        this._clearChangesIfEqual('allow_refund_of_exchanged');
    }

    public setAllowExchangeOfExchanged(v: boolean) {
        this._changes.allow_exchange_of_exchanged = v;
        this._clearChangesIfEqual('allow_exchange_of_exchanged');
    }

    public setShowDisabledResolution(v: boolean) {
        this._changes.show_disabled_resolution = v;
        this._clearChangesIfEqual('show_disabled_resolution');
    }

    public setAutoPopulateRefundAmount(v: boolean) {
        this._changes.auto_populate_refund_amount = v;
        this._clearChangesIfEqual('auto_populate_refund_amount');
    }

    public setEnableGiftReturns(v: boolean) {
        this._changes.enable_gift_returns = v;
        this._clearChangesIfEqual('enable_gift_returns');
    }

    public setIsValidationEnabled(v: boolean) {
        this._changes.is_validation_enabled = v;
        if (!v) {
            this.setIsRestockNoteMandatory(false);
        }
        this._clearChangesIfEqual('is_validation_enabled');
    }

    public setIsRestockNoteMandatory(v: boolean) {
        this._changes.is_restock_note_mandatory = v;
        this._clearChangesIfEqual('is_restock_note_mandatory');
    }

    public setIsValidateAllEnabled(v: boolean) {
        this._changes.is_validate_all_enabled = v;
        this._clearChangesIfEqual('is_validate_all_enabled');
    }

    public setIsBxgyEnabled(v: boolean) {
        this._changes.is_bxgy_enabled = v;
        this._clearChangesIfEqual('is_bxgy_enabled');
    }

    public setBxgyBreakable(v: boolean) {
        this._changes.bxgy_breakable = v;
        this._clearChangesIfEqual('bxgy_breakable');
    }

    public setApiCollectionsAsArray(v: boolean) {
        this._changes.api_strict_types = v;
        this._clearChangesIfEqual('api_strict_types');
    }

    public setBxgyCompensateBy(v: BXGY_COMPENSATE_BY) {
        this._changes.bxgy_compensate_by = v;
        this._clearChangesIfEqual('bxgy_compensate_by');
    }

    public setOrderDeliveryFallbackDays(v: number) {
        if (String(v).indexOf('.') !== -1) {
            return;
        }
        this._changes.order_delivery_fallback_days = v;
        this._clearChangesIfEqual('order_delivery_fallback_days');
    }

    public setShipmentInTransitFallback(v: number) {
        this._changes.shipment_in_transit_fallback = v;
        this._clearChangesIfEqual('shipment_in_transit_fallback');
    }

    public setIncludeShippingCostInRefund(v: INCLUDE_SHIPPING_COST_IN_REFUND) {
        this._changes.include_shipping_cost_in_refund = v;
        this._clearChangesIfEqual('include_shipping_cost_in_refund');
    }

    public setIncludeProductDutiesInRefund(v: INCLUDE_PRODUCT_DUTIES_IN_REFUND) {
        this._changes.include_product_duties_in_refund = v;
        this._clearChangesIfEqual('include_product_duties_in_refund');
    }

    public setIncludeShippingCostInRefundWhenAllItemsAreReturned(v: boolean) {
        this._changes.include_shipping_cost_in_refund_for_all_items_only = v;
        this._clearChangesIfEqual('include_shipping_cost_in_refund_for_all_items_only');
    }

    public setRequestCustomersConfirmPolicy(v: boolean) {
        this._changes.request_customers_confirm_policy = v;
        this._clearChangesIfEqual('request_customers_confirm_policy');
    }

    public setLineItemRefundTransactions(v: LINE_ITEMS_REFUND_TRANSACTIONS) {
        this._changes.line_items_refund_transactions = v;
        this._clearChangesIfEqual('line_items_refund_transactions');
    }

    public setExchangeBalancing(v: EXCHANGE_BALANCING) {
        this._changes.exchange_balancing = v;
        this._clearChangesIfEqual('exchange_balancing');
    }

    public setProductExchangePriceChange(v: PRICE_CHANGE_SETTING) {
        this._changes.product_exchange_changes_to_new_item_price = v;
        this._clearChangesIfEqual('product_exchange_changes_to_new_item_price');
    }
    public setVariantExchangePriceChange(v: PRICE_CHANGE_SETTING) {
        this._changes.variant_exchange_changes_to_new_item_price = v;
        this._clearChangesIfEqual('variant_exchange_changes_to_new_item_price');
    }

    public setExchangeDiscountType(v: EXCHANGE_DISCOUNT_TYPE) {
        this._changes.exchange_discount_type = v;
        this._clearChangesIfEqual('exchange_discount_type');
    }

    public setCapturePaymentTrigger(v: AUTO_PROCESS_TRIGGER) {
        this._changes.capture_payment_trigger = v;
        this._clearChangesIfEqual('capture_payment_trigger');
    }

    public setCapturePaymentValidatedQualifier(v: AUTO_PROCESS_VALIDATED_QUALIFIER) {
        this._changes.capture_payment_validated_qualifier = v;
        this._clearChangesIfEqual('capture_payment_validated_qualifier');
    }

    public setRequireInstantExchange(v: boolean) {
        this._changes.require_instant_exchange = v;
        this._clearChangesIfEqual('require_instant_exchange');
    }

    public setEnablePortalCheckout(v: boolean) {
        this._changes.enable_portal_checkout = v;
        this._clearChangesIfEqual('enable_portal_checkout');
    }

    public setMinimumPaymentThreshold(v: number) {
        this._changes.minimum_payment_threshold = v;
		this._clearChangesIfEqual('minimum_payment_threshold');
    }

    public setUsePreAuthorize(v: boolean) {
        this._changes.use_pre_authorize = v;
        this._clearChangesIfEqual('use_pre_authorize');
    }

    public setPortalIsolationMode(v: ISOLATION_MODE) {
        this._changes.portal_isolation_mode = v;
        this._clearChangesIfEqual('portal_isolation_mode');
    }

    public setAutoSelectSamePolicy(v: boolean) {
        this._changes.auto_select_same_policy = v;
        this._clearChangesIfEqual('auto_select_same_policy');
    }

    public setStoreCreditsRefundLimit(v: number) {
        this._changes.store_credits_refund_limit_percentage = v;
        this._clearChangesIfEqual('store_credits_refund_limit_percentage');
    }

    public setGiftCardRefundLimit(v: number) {
        this._changes.gift_card_refund_limit_percentage = v;
        this._clearChangesIfEqual('gift_card_refund_limit_percentage');
    }

    public setIsCancelShipmentLabelEnabled(v: boolean) {
        this._changes.is_cancel_shipment_label_enabled = v;
        this._clearChangesIfEqual('is_cancel_shipment_label_enabled');
    }

    public setShowShipToAddress(v: boolean) {
        this._changes.show_ship_to_address = v;
        this._clearChangesIfEqual('show_ship_to_address');
    }

    public setEnablePortalV2(v: boolean) {
        this._changes.enable_portal_v2 = v;
        this._clearChangesIfEqual('enable_portal_v2');
    }

    public setStoreFavicon(type: string, base64_content: string) {
        this._changes._store_new_favicon = {
            base64_content,
            type,
        };
        delete this._changes._store_delete_favicon;
    }

    public deleteStoreFavicon() {
        this._changes._store_delete_favicon = !!this._model.store_favicon;
        delete this._changes._store_new_favicon;
    }

    public setStorePageTitle(value: string) {
        this._changes.store_page_title = value;
        this._clearChangesIfEqual("store_page_title");
    }

    public setDiscreetSenderEnabled(value: boolean) {
        this._changes.enabled_discreet_sender = value;
        this._clearChangesIfEqual("enabled_discreet_sender");
    }

    public hasChanges(): boolean {
        const hasRefundSettingsChanges = this._refundSettings.some(store => store.hasChanges());
        const hasSearchProductsByChanges = this._search_by.hasChanges();
        const hasBrowseByCollectionChanges = this._browseByCollection.hasChanges();
        const hasPortalCheckoutChanges = this._instantExchangeStore.hasChanges();
        const hasReturngoFeesSettingsChanges = this._returngoFeesSettings.some(store => store.hasChanges());
        const hasSwitchToExchangeChanges = this._switchToExchangeStore.hasChanges();
        const hasCapDiscountChanges = this._capDiscountStore.hasChanges();
        const hasRefundToBankSettingChanges = this._refundToBankSettingStore.hasChanges();
        const hasCreateRmaDefaultSettingsChanges = this._createRmaDefaultSettingsEditorStore.hasChanges();
        const hasRmaLevelBonusLimitSettingChanges = this._rmaLevelBonusLimitStore.hasChanges();
        const hasNotificationOnlyModeSettingsChanges = this._notificationOnlyModeSettingsStore.hasChanges();
        const hasTimezoneChanges = this._timezoneStore.hasChanges();
        const hasCancelInactiveRmasSettingsChanges = Object.values(this._cancel_inactive_rmas_settings).some(s => s?.hasChanges()) ?? false;
        const hasAnyToAnyExchangesChanges = this._anyToAnyExchangesStore.hasChanges();
        const hasExchangeLocationsChanges = this._exchangeLocationsStore.hasChanges();

        return (
            !!Object.keys(this._changes).length ||
            hasRefundSettingsChanges ||
            hasSearchProductsByChanges ||
            hasBrowseByCollectionChanges ||
            hasPortalCheckoutChanges ||
            hasReturngoFeesSettingsChanges ||
            hasSwitchToExchangeChanges ||
            hasCapDiscountChanges ||
            hasRefundToBankSettingChanges ||
            hasCreateRmaDefaultSettingsChanges ||
            hasRmaLevelBonusLimitSettingChanges ||
            hasNotificationOnlyModeSettingsChanges ||
            hasTimezoneChanges ||
            hasCancelInactiveRmasSettingsChanges ||
            hasAnyToAnyExchangesChanges ||
            hasExchangeLocationsChanges
        );
    }

    public getUpdatedModel(): ISetting {
        const refund_settings = this._refundSettings.map(store => store.getUpdatedModel());
        const returngo_fees_settings = this._returngoFeesSettings.map(store => store.getUpdatedModel());
        const search_product_by = this._search_by.getUpdatedModel();
        const browse_by_collection = this._browseByCollection.getUpdatedModel();
        const instant_exchange_setting = this._instantExchangeStore.getUpdatedModel();
        const switch_to_exchange_setting = this._switchToExchangeStore.getUpdatedModel();
        const cap_discount_setting = this._capDiscountStore.getUpdatedModel();
        const refund_to_bank_setting = this._refundToBankSettingStore.getUpdatedModel();
        const create_rma_settings = this._createRmaDefaultSettingsEditorStore.getUpdatedModel();
        const rma_level_bonus_limit_setting = this._rmaLevelBonusLimitStore.getUpdatedModel();
        const notification_only_mode_setting = this._notificationOnlyModeSettingsStore.getUpdatedModel();
        const timezoneModel = this._timezoneStore.getUpdatedModel();
        const any_to_any_exchanges_setting = this._anyToAnyExchangesStore.getUpdatedModel();
        const exchange_locations_model = this._exchangeLocationsStore.getUpdatedModel();

        const updatedModel = {
            ...this._model,
            ...this._changes,
            refund_settings,
            search_product_by,
            ...browse_by_collection,
            instant_exchange_setting,
            returngo_fees_settings,
            switch_to_exchange_setting,
            cap_discount_setting,
            refund_to_bank_setting,
            create_rma_settings,
            rma_level_bonus_limit_setting,
            notification_only_mode_setting,
            ...timezoneModel,
            any_to_any_exchanges_setting,
            ...exchange_locations_model,
        };

        updatedModel.cancel_inactive_rmas_settings = Object.values(this._cancel_inactive_rmas_settings).map(s => s?.getUpdatedModel()).filter(s => !!s) as ICancelInactiveRmasSettings[];

        return updatedModel;
    }

    private getUpdatedReturnScoreModel(): IReturnScoreSetting {
        const updatedModel = {
            return_paid_by: this.returnPaidBy,
            store_cost_margin: this.storeCostMargin,
            returned_item_value: this.returnedItemValue,
            cost_of_handling_a_return: this.costOfHandlingAReturn,
        };
        return updatedModel;
    }

    public discardChanges(): void {
        this._search_by.discardChanges();
        this._browseByCollection.discardChanges();
        this._changes = {} as ISettingChanges;
        this._refundSettings.forEach(function (refundSetting) {
            refundSetting.discardChanges();
        });
        this._instantExchangeStore.discardChanges();
        this._returngoFeesSettings.forEach(function (returngoFeesSetting) {
            returngoFeesSetting.discardChanges();
        });
        this._switchToExchangeStore.discardChanges();
        this._capDiscountStore.discardChanges();
        this._refundToBankSettingStore.discardChanges();
        this._createRmaDefaultSettingsEditorStore.discardChanges();
        this._rmaLevelBonusLimitStore.discardChanges();
        this._notificationOnlyModeSettingsStore.discardChanges();
        this._timezoneStore.discardChanges();
        this._anyToAnyExchangesStore.discardChanges();
        this._exchangeLocationsStore.discardChanges();

        Object.values(this._cancel_inactive_rmas_settings).forEach(s => s?.discardChanges());
    }

    private _clearChangesIfEqual(key: keyof ISetting): void {
        if (this._changes[key] === this._model[key]) {
            delete this._changes[key];
        }
    }

    public async save() {
        const updatedModel: WithOld<ISetting> = this.getUpdatedModel();
        updatedModel.__old = this._model;

        this._isSaving = true;

        try {
            const responseStoreSettings = await this.service.saveStoreSettings(updatedModel);
            runInAction(() => {
                this.setModel(responseStoreSettings);
                this._isSaving = false;
            });
        } catch (error) {
            runInAction(() => {
                this._isSaving = false;
            });
        }
    }

    public async saveReturnScoreSettings() {
        const updatedReturnScoreModel = this.getUpdatedReturnScoreModel();
        this._isReturnScoreSaving = true;

        try {
            const responseReturnScoreSettings = await this.service.saveReturnScoreSettings(updatedReturnScoreModel);
            runInAction(() => {
                this.discardChanges();
                this._model = { ...this._model, ...responseReturnScoreSettings };
                this._isReturnScoreSaving = false;
            });
        } catch (error) {
            runInAction(() => {
                this._isReturnScoreSaving = false;
            });
        }
    }

    public getReturnScoreSettingsState() {
        let totalSettings = 0;
        let filledSettings = 0;

        // check storeCostMargin
        totalSettings++;
        if (this._model.store_cost_margin && this._model.store_cost_margin !== '') {
            filledSettings++;
        }

        // check returnPaidBy
        totalSettings++;
        if (this._model.return_paid_by && this._model.return_paid_by !== '') {
            filledSettings++;
        }

        // check returnedItemValue
        totalSettings++;
        if (this._model.returned_item_value && this._model.returned_item_value >= 0) {
            filledSettings++;
        }

        // check costOfHandlingAReturn
        totalSettings++;
        if (this._model.cost_of_handling_a_return && this._model.cost_of_handling_a_return >= 0) {
            filledSettings++;
        }

        return {
            total: totalSettings,
            filled: filledSettings
        }
    }

    public getLabelFeeRoundingCondition(): LABEL_FEE_ROUNDING {
        return this._changes.label_fee_rounding_condition ?? this._model.label_fee_rounding_condition;
    }

    public getLabelFeeRoundingAmount(): number|null {
        return this._changes.label_fee_rounding_amount ?? this._model.label_fee_rounding_amount;
    }

    public setLabelFeeRoundingAmount(v: number|null): void {
        this._changes.label_fee_rounding_amount = v;
        this._clearChangesIfEqual('label_fee_rounding_amount');
    }

    public setLabelFeeRoundingCondition(v: SingleValue<IOption>): void {
        this._changes.label_fee_rounding_condition = v?.value as LABEL_FEE_ROUNDING;
        this._clearChangesIfEqual('label_fee_rounding_condition');
        if (this.getLabelFeeRoundingCondition() === LABEL_FEE_ROUNDING.LABEL_FEE_NO_ROUNDING) {
            this.setLabelFeeRoundingAmount(null);
        }
    }

    private _getBase64Str(
        data: {
            base64_content: string;
            type: string;
        } | null
    ): string | null {
        if (!data) {
            return null;
        }
        return `data:${data.type};base64,${data.base64_content}`;
    }
}

export default SettingStore;

function ensureValidModel(data: ISetting): ISetting {
    const toBool = (v: undefined | any) => {
        if (typeof v === 'string') {
            return Boolean(Number(v));
        }
        return Boolean(v);
    };
    const toNum = (v: undefined | any) => v !== undefined ? Number(v) : v;

    const returnCreditsExpire = toNum(data.return_credits_expire);
    const giftCardsExpire = toNum(data.gift_cards_expire);

    return {
        ...data,
        support_multiple_currencies: toBool(data.support_multiple_currencies),
        allow_portal_redirection: toBool(data.allow_portal_redirection ?? false),
        is_rounding: toBool(data.is_rounding),
        allow_multiple_return_requests: toBool(data.allow_multiple_return_requests),
        enable_start_another_return: toBool(data.enable_start_another_return),
        show_disabled_resolution: toBool(data.show_disabled_resolution),
        disable_order_notes: toBool(data.disable_order_notes),
        allow_refund_of_exchanged: toBool(data.allow_refund_of_exchanged),
        allow_exchange_of_exchanged: toBool(data.allow_exchange_of_exchanged),
        auto_populate_refund_amount: toBool(data.auto_populate_refund_amount),
        enable_gift_returns: toBool(data.enable_gift_returns),
        is_validation_enabled: toBool(data.is_validation_enabled),
        is_restock_note_mandatory: toBool(data.is_restock_note_mandatory),
        is_validate_all_enabled: toBool(data.is_validate_all_enabled),
        is_bxgy_enabled: toBool(data.is_bxgy_enabled),
        order_delivery_fallback_days: toNum(data.order_delivery_fallback_days),
        shipment_in_transit_fallback: toNum(data.shipment_in_transit_fallback),
        api_strict_types: toBool(data.api_strict_types),
        return_credits_expire: !isNaN(returnCreditsExpire) ? returnCreditsExpire : 0,
        gift_cards_expire: !isNaN(giftCardsExpire) ? giftCardsExpire : 0,
        bxgy_breakable: toBool((data.bxgy_breakable)),
        request_customers_confirm_policy: toBool(data.request_customers_confirm_policy),
        require_instant_exchange: toBool(data.require_instant_exchange),
        auto_select_same_policy: toBool(data.auto_select_same_policy),
        cancel_inactive_rmas: toBoolean(data.cancel_inactive_rmas),
        is_cancel_shipment_label_enabled: toBoolean(data.is_cancel_shipment_label_enabled),
        gift_card_refund_limit_percentage: toNum(data.gift_card_refund_limit_percentage),
        store_credits_refund_limit_percentage: toNum(data.store_credits_refund_limit_percentage),
        default_portal_resolution_id: data.default_portal_resolution_id == null
            ? null
            : toNum(data.default_portal_resolution_id),
    };
}

function isStringContainsInteger(value: any): boolean {
    return !isNaN(Number(value)) && value.indexOf('.') === -1;
}
